import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { ListPickerComponent } from '@modules/shared/list-picker/list-picker.component';
import { Store } from '@ngrx/store';
import { selectUserEnabledClientDepartments } from '@shared/store/departments/departments.selectors';
import { combineLatest, first, map, Observable } from 'rxjs';
import { ClientData } from 'src/app/shared/models/base/client-data';
import { ListItem } from 'src/app/shared/models/helpers/list-item';
import { selectClients } from 'src/app/shared/store/clients/clients.selectors';
import { selectWriteAccessClientIds } from 'src/app/shared/store/licenses/licenses.selectors';
import { environment } from 'src/environments/environment';
import { ClientPickerComponent } from '../../../client/client-picker/client-picker.component';

@Component({
  selector: 'pxc-department-picker',
  templateUrl: './department-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ClientPickerComponent, ListPickerComponent, TranslocoDirective],
})
export class DepartmentPickerComponent implements OnChanges {
  @Input() clientCode: string;
  @Input() departmentIds: number[] = [];
  @Input() isClientChoiceDisabled = false;
  @Input() isDepartmentChoiceDisabled = false;

  @Output() readonly clientCodeChange = new EventEmitter<string>();
  @Output() readonly departmentIdsChange = new EventEmitter<number[]>();

  private readonly store = inject(Store);
  private readonly authenticationService = inject(AuthenticationService);

  commonClientCode = environment.commonClientCode;

  clients$ = combineLatest([this.store.select(selectClients), this.store.select(selectWriteAccessClientIds)]).pipe(
    map(([clients, writeAccessIds]) =>
      Object.values(clients)
        .filter((client) => writeAccessIds.includes(client.id))
        .reduce((acc, client) => ({ ...acc, [client.code]: client }), {} as { [clientCode: string]: ClientData }),
    ),
  );

  departments$: Observable<ListItem[]>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.clientCode && changes.clientCode.currentValue !== changes.clientCode.previousValue) {
      this.departments$ = this.store
        .select(selectUserEnabledClientDepartments(this.clientCode, this.authenticationService.user.id))
        .pipe(
          map((departments) =>
            departments.map((department) => ({
              value: department.id,
              display: department.name,
            })),
          ),
        );

      if (!changes.clientCode.firstChange) {
        this.departments$.pipe(first()).subscribe((departments) => {
          if (departments.length === 1) {
            this.departmentIdsChange.emit([departments[0].value]);
          } else {
            this.departmentIdsChange.emit([]);
          }
        });
      }
    }
  }
}
